import React from 'react';
import { Image, RefreshCw, UploadCloud, Trash2, XCircle } from 'lucide-react';

const SplashSection = ({
  theme,
  styles,
  selectedCompany,
  splashInfo,
  loadingSplash,
  uploadingSplash,
  splashPreviewUrl,
  splashUrlInput,
  setSplashUrlInput,
  splashActive,
  iconPreviewUrl,
  iconUrlInput,
  setIconUrlInput,
  setIconPreviewUrl,
  iconFile,
  splashFile,
  splashSectionRef,
  onSplashFileChange,
  onClearSplash,
  onIconFileChange,
  onClearIcon,
  onSave,
  onDeactivate,
  onToggleActive,
}) => {
  return (
    <div ref={splashSectionRef} style={styles.splashCard}>
      {/* Header */}
      <div style={styles.splashHeader}>
        <div>
          <h4 style={{
            fontSize: "16px",
            fontWeight: "600",
            color: theme === "light" ? "#0f172a" : "#ffffff",
            margin: 0,
            marginBottom: "4px",
          }}>
            Client Splash Screen
          </h4>
          <p style={{
            fontSize: "12px",
            color: theme === "light" ? "#475569" : "rgba(255, 255, 255, 0.7)",
            margin: 0,
          }}>
            Displayed after login for {selectedCompany}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{
            fontSize: "12px",
            fontWeight: "600",
            color: splashActive
              ? theme === "light" ? "#0f172a" : "rgba(255,255,255,0.85)"
              : "#ef4444",
          }}>
            {splashActive ? 'Active' : 'Inactive'}
          </span>
          <div style={styles.toggle(splashActive)} onClick={onToggleActive}>
            <div style={styles.toggleKnob(splashActive)} />
          </div>
        </div>
      </div>

      {/* Splash Preview */}
      {loadingSplash ? (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          padding: "24px",
          color: theme === "light" ? "#0f172a" : "rgba(255,255,255,0.7)",
        }}>
          <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
          Loading splash details...
        </div>
      ) : (
        <div style={{ marginBottom: "16px" }}>
          {splashPreviewUrl ? (
            <img src={splashPreviewUrl} alt="Splash preview" style={styles.splashPreview} />
          ) : (
            <div style={{
              ...styles.splashPreview,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: theme === "light" ? "#0f172a" : "rgba(255,255,255,0.6)",
              gap: "8px",
              fontSize: "13px",
            }}>
              <Image size={28} />
              No splash GIF configured
            </div>
          )}
        </div>
      )}

      {/* GIF Inputs */}
      <div style={styles.splashInputRow}>
        <div>
          <label style={{
            display: "block",
            fontSize: "12px",
            fontWeight: "600",
            color: theme === "light" ? "#0f172a" : "rgba(255,255,255,0.85)",
            marginBottom: "6px",
          }}>
            Upload GIF file
          </label>
          <input
            type="file"
            accept="image/gif"
            onChange={onSplashFileChange}
            disabled={uploadingSplash}
            style={{ ...styles.splashInput, padding: "8px" }}
          />
        </div>
        <div>
          <label style={{
            display: "block",
            fontSize: "12px",
            fontWeight: "600",
            color: theme === "light" ? "#0f172a" : "rgba(255,255,255,0.85)",
            marginBottom: "6px",
          }}>
            Or link to an external GIF
          </label>
          <input
            type="text"
            value={splashUrlInput}
            onChange={(e) => setSplashUrlInput(e.target.value)}
            placeholder="https://example.com/splash.gif"
            style={styles.splashInput}
            disabled={uploadingSplash}
          />
        </div>
      </div>

      <div style={{
        fontSize: "12px",
        color: theme === "light" ? "#475569" : "rgba(255,255,255,0.6)",
        marginTop: "12px",
      }}>
        Only GIF files are supported. Uploading a file will override the URL.
      </div>

      {/* Icon Section */}
      <div style={styles.iconContainer}>
        <div style={styles.iconHeaderRow}>
          <div>
            <h5 style={styles.iconTitle(theme)}>Client Badge Icon</h5>
            <p style={styles.iconSubtitle(theme)}>
              Appears at the bottom-right badge for {selectedCompany}
            </p>
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          {iconPreviewUrl ? (
            <img src={iconPreviewUrl} alt="Icon preview" style={styles.iconPreview} />
          ) : (
            <div style={styles.iconPlaceholder(theme)}>
              <Image size={22} />
              No icon selected
            </div>
          )}
        </div>

        <div style={styles.iconInputRow}>
          <div>
            <label style={styles.iconLabel(theme)}>Upload icon file</label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
              onChange={onIconFileChange}
              disabled={uploadingSplash}
              style={{ ...styles.splashInput, padding: "8px" }}
            />
          </div>
          <div>
            <label style={styles.iconLabel(theme)}>Or link to an external icon</label>
            <input
              type="text"
              value={iconUrlInput}
              onChange={(e) => {
                const value = e.target.value;
                setIconUrlInput(value);
                if (!iconFile) {
                  setIconPreviewUrl(value.trim() ? value.trim() : null);
                }
              }}
              placeholder="https://example.com/icon.png"
              style={styles.splashInput}
              disabled={uploadingSplash}
            />
          </div>
        </div>

        <div style={styles.iconHint(theme)}>
          PNG, JPG, JPEG, SVG, WEBP, or ICO up to 5MB. Leave blank to remove the icon.
        </div>
      </div>

      {/* Actions */}
      <div style={styles.splashActions}>
        <button
          style={{ ...styles.button, marginTop: 0, padding: "10px 20px" }}
          onClick={onSave}
          disabled={uploadingSplash || loadingSplash}
        >
          {uploadingSplash ? (
            <>
              <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
              Saving...
            </>
          ) : (
            <>
              <UploadCloud size={18} />
              Save Splash & Icon
            </>
          )}
        </button>

        {(splashFile || splashUrlInput || iconFile || iconUrlInput) && (
          <button
            style={styles.secondaryButton}
            onClick={() => { onClearSplash(); onClearIcon(); }}
            disabled={uploadingSplash}
          >
            <Trash2 size={16} />
            Clear Selection
          </button>
        )}

        {splashInfo && splashInfo.isActive && (
          <button
            style={{
              ...styles.secondaryButton,
              background: "rgba(239, 68, 68, 0.15)",
              color: "#ef4444",
            }}
            onClick={onDeactivate}
            disabled={uploadingSplash || loadingSplash}
          >
            <XCircle size={16} />
            Deactivate Current GIF
          </button>
        )}
      </div>
    </div>
  );
};

export default SplashSection;